import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Play, 
  Square, 
  RefreshCw, 
  FileText, 
  Clock, 
  CheckCircle2,
  AlertCircle,
  TrendingUp
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [statsRes, statusRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { headers }),
        axios.get(`${API}/scheduler/status`, { headers })
      ]);
      setStats(statsRes.data);
      setSchedulerStatus(statusRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRunOnce = async () => {
    setActionLoading(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/scheduler/run-once`, {}, { headers });
      toast.success("Content generation started");
      setTimeout(fetchData, 2000);
    } catch (error) {
      toast.error("Failed to start generation");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRunForever = async () => {
    setActionLoading(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/scheduler/run-forever`, {}, { headers });
      toast.success("Continuous generation started");
      fetchData();
    } catch (error) {
      toast.error("Failed to start scheduler");
    } finally {
      setActionLoading(false);
    }
  };

  const handleStop = async () => {
    setActionLoading(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/scheduler/stop`, {}, { headers });
      toast.success("Scheduler stopped");
      fetchData();
    } catch (error) {
      toast.error("Failed to stop scheduler");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSeedQueue = async () => {
    setActionLoading(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${API}/inventory/seed?confirm=true&city_tiers=1,2&venue_priorities=1,2&limit=50`, {}, { headers });
      const added = res.data.added_to_queue || 0;
      const skipped = res.data.skipped_duplicate || 0;
      if (added > 0) {
        toast.success(`Added ${added} new keywords to queue`);
      } else if (skipped > 0) {
        toast.info(`Queue already has ${skipped} keywords. No new ones added.`);
      } else {
        toast.info('No new keywords to add');
      }
      fetchData();
    } catch (error) {
      console.error('Seed error:', error);
      toast.error("Failed to seed queue: " + (error.response?.data?.detail || error.message));
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-full">
        <RefreshCw className="h-6 w-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-mono font-medium text-white">Dashboard</h1>
          <p className="text-sm text-zinc-500">SEO content generation overview</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSeedQueue}
            disabled={actionLoading}
            data-testid="seed-queue-btn"
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          >
            Seed Demo Content
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRunOnce}
            disabled={actionLoading}
            data-testid="run-once-btn"
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          >
            <Play className="h-4 w-4 mr-1" />
            Run Once
          </Button>
          {schedulerStatus?.running ? (
            <Button
              variant="destructive"
              size="sm"
              onClick={handleStop}
              disabled={actionLoading}
              data-testid="stop-btn"
            >
              <Square className="h-4 w-4 mr-1" />
              Stop
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={handleRunForever}
              disabled={actionLoading}
              data-testid="run-forever-btn"
              className="bg-white text-black hover:bg-zinc-200"
            >
              <Play className="h-4 w-4 mr-1" />
              Run Forever
            </Button>
          )}
        </div>
      </div>

      {/* Scheduler Status */}
      {schedulerStatus?.running && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-md p-3 flex items-center gap-2">
          <div className="h-2 w-2 bg-emerald-500 rounded-full animate-pulse" />
          <span className="text-emerald-400 text-sm font-mono">
            Scheduler running • Interval: {schedulerStatus.interval_minutes}min
          </span>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="stats-grid">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs text-zinc-500 uppercase tracking-wider flex items-center gap-2">
              <Clock className="h-3 w-3" />
              Queue Pending
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="font-mono text-3xl font-semibold text-white" data-testid="stat-pending">
              {stats?.queue?.pending || 0}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs text-zinc-500 uppercase tracking-wider flex items-center gap-2">
              <CheckCircle2 className="h-3 w-3" />
              Published
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="font-mono text-3xl font-semibold text-emerald-400" data-testid="stat-published">
              {stats?.pages?.published || 0}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs text-zinc-500 uppercase tracking-wider flex items-center gap-2">
              <AlertCircle className="h-3 w-3" />
              QA Failed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="font-mono text-3xl font-semibold text-red-400" data-testid="stat-failed">
              {stats?.pages?.qa_failed || 0}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs text-zinc-500 uppercase tracking-wider flex items-center gap-2">
              <TrendingUp className="h-3 w-3" />
              Total Words
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="font-mono text-3xl font-semibold text-white" data-testid="stat-words">
              {(stats?.pages?.total_words || 0).toLocaleString()}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Domain Stats */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-sm font-mono text-zinc-300">Pages by Domain</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            {Object.entries(stats?.by_domain || {}).map(([domain, count]) => (
              <div key={domain} className="bg-zinc-800 rounded-md p-4">
                <div className="text-xs text-zinc-500 truncate mb-1">{domain}</div>
                <div className="font-mono text-2xl text-white">{count}</div>
              </div>
            ))}
            {Object.keys(stats?.by_domain || {}).length === 0 && (
              <div className="col-span-3 text-center py-8 text-zinc-500">
                No pages published yet
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-sm font-mono text-zinc-300">Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {(stats?.recent_activity || []).map((log, idx) => (
              <div 
                key={idx}
                className="flex items-start gap-3 text-sm py-2 border-b border-zinc-800 last:border-0"
              >
                <span className={`font-mono text-xs px-2 py-0.5 rounded ${
                  log.level === 'ERROR' ? 'bg-red-500/20 text-red-400' :
                  log.level === 'WARNING' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-blue-500/20 text-blue-400'
                }`}>
                  {log.level}
                </span>
                <span className="text-zinc-400 flex-1">{log.message}</span>
                <span className="text-zinc-600 text-xs font-mono">
                  {new Date(log.created_at).toLocaleTimeString()}
                </span>
              </div>
            ))}
            {(stats?.recent_activity || []).length === 0 && (
              <div className="text-center py-8 text-zinc-500">
                No recent activity
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
